import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { supabase, checkSupabaseConnection, executeQuery } from './lib/supabase';
import { AuthForm } from './components/AuthForm';
import { EmailVerification } from './components/EmailVerification';
import { Navigation } from './components/Navigation';
import { ManagementDocuments } from './components/sections/ManagementDocuments';
import { Projects } from './components/sections/Projects';
import { Units } from './components/sections/Units';
import { Sessions } from './components/sections/Sessions';
import { Evaluation } from './components/sections/Evaluation';
import { Evidence } from './components/sections/Evidence';
import { MyFiles } from './components/sections/MyFiles';
import { Home } from './components/sections/Home';
import PrivacyPolicy from './components/sections/PrivacyPolicy';
import { ErrorBoundary } from './components/ErrorBoundary';

function App() {
  const [session, setSession] = useState<any>(null);
  const [files, setFiles] = useState<Array<{ id: string; name: string; webViewLink: string }>>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<{
    isConnected: boolean;
    error?: string;
    details?: any;
  }>({ isConnected: true });
  const [connectionChecked, setConnectionChecked] = useState(false);

  // Helper function to detect webcontainer environment
  const isWebContainerEnvironment = () => {
    return window.location.hostname.includes('webcontainer') || 
           window.location.hostname.includes('local-credentialless') ||
           window.location.hostname.includes('stackblitz') ||
           window.location.hostname.includes('codesandbox') ||
           window.location.hostname.includes('localhost');
  };

  useEffect(() => {
    const checkConnection = async () => {
      try {
        // If we're in a webcontainer environment, skip the connection check
        if (isWebContainerEnvironment()) {
          setConnectionStatus({ 
            isConnected: true,
            error: 'Ejecutándose en modo de desarrollo - Algunas funciones pueden estar limitadas'
          });
          setConnectionChecked(true);
          setError('Modo de desarrollo: Algunas funciones pueden estar limitadas debido a restricciones del entorno');
          return;
        }

        const status = await checkSupabaseConnection();
        setConnectionStatus(status);
        setConnectionChecked(true);
        
        if (!status.isConnected) {
          console.error('Supabase connection error:', status);
          setError('Error de conexión: Algunas funciones pueden estar limitadas');
        }
      } catch (error) {
        console.error('Connection check failed:', error);
        
        // If connection check fails completely, enable offline mode
        setConnectionStatus({
          isConnected: true,
          error: 'Ejecutándose en modo sin conexión'
        });
        setConnectionChecked(true);
        setError('Modo sin conexión: Algunas funciones pueden estar limitadas');
      }
    };

    checkConnection();
  }, []);

  useEffect(() => {
    // Only try to get session if connection is working and not in webcontainer
    if (!connectionStatus.isConnected || isWebContainerEnvironment()) return;

    const initAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setSession(session);
      } catch (error) {
        console.error('Error getting session:', error);
        setError('Error de autenticación. Por favor, recarga la página.');
      }
    };

    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, [connectionStatus.isConnected]);

  // Show loading state while checking connection
  if (!connectionChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <h2 className="mt-6 text-center text-xl font-semibold text-gray-900">
              Iniciando aplicación...
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Configurando el entorno
            </p>
          </div>
        </div>
      </div>
    );
  }

  // In webcontainer environment, skip authentication and go directly to the main app
  if (isWebContainerEnvironment()) {
    return (
      <ErrorBoundary>
        <BrowserRouter>
          <div className="min-h-screen bg-gray-100">
            <Navigation />
            <div className="md:ml-64">
              {error && (
                <div className="m-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md text-yellow-800">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm">
                        {error}
                      </p>
                    </div>
                    <div className="ml-auto pl-3">
                      <div className="-mx-1.5 -my-1.5">
                        <button
                          onClick={() => setError(null)}
                          className="inline-flex bg-yellow-50 rounded-md p-1.5 text-yellow-500 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-yellow-50 focus:ring-yellow-600"
                        >
                          <span className="sr-only">Dismiss</span>
                          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/management" element={<ManagementDocuments />} />
                <Route path="/projects" element={<Projects />} />
                <Route path="/units" element={<Units />} />
                <Route path="/sessions" element={<Sessions />} />
                <Route path="/evaluation" element={<Evaluation />} />
                <Route path="/evidence" element={<Evidence />} />
                <Route path="/my-files" element={<MyFiles files={files} />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/auth/callback" element={<div>Authenticating...</div>} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          </div>
        </BrowserRouter>
      </ErrorBoundary>
    );
  }

  if (!connectionStatus.isConnected) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Error de Conexión
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              No se puede conectar con el servidor.
            </p>
            
            <div className="mt-4 p-4 bg-red-50 rounded-md">
              <p className="text-sm text-red-700">
                <strong>Error:</strong> {connectionStatus.error}
              </p>
            </div>
            
            <div className="mt-4 space-y-2">
              <button
                onClick={async () => {
                  setConnectionChecked(false);
                  const status = await checkSupabaseConnection();
                  setConnectionStatus(status);
                  setConnectionChecked(true);
                }}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Reintentar Conexión
              </button>
              
              <button
                onClick={() => {
                  setConnectionStatus({ isConnected: true });
                  setError('Modo sin conexión: Algunas funciones pueden estar limitadas');
                }}
                className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Continuar sin Conexión
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <ErrorBoundary>
        <BrowserRouter>
          <Routes>
            <Route path="/auth/callback" element={
              <EmailVerification 
                status="success"
                message="¡Tu correo electrónico ha sido verificado exitosamente! Ahora puedes iniciar sesión."
              />
            } />
            <Route path="*" element={<AuthForm />} />
          </Routes>
        </BrowserRouter>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <div className="min-h-screen bg-gray-100">
          <Navigation />
          <div className="md:ml-64">
            {error && (
              <div className="m-4 p-4 bg-red-50 border border-red-200 rounded-md text-red-700">
                {error}
                <button
                  onClick={() => setError(null)}
                  className="ml-2 text-sm underline hover:no-underline"
                >
                  Dismiss
                </button>
              </div>
            )}
            
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/management" element={<ManagementDocuments />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/units" element={<Units />} />
              <Route path="/sessions" element={<Sessions />} />
              <Route path="/evaluation" element={<Evaluation />} />
              <Route path="/evidence" element={<Evidence />} />
              <Route path="/my-files" element={<MyFiles files={files} />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/auth/callback" element={<div>Authenticating...</div>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </div>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;